// frontend/src/utils/betFormatting.js
// Single source of truth for turning a bet's raw fields (market_type, selection,
// player_name, selection_type, point) into the text shown to the user — used
// both when a bet is first placed (building the description that gets stored)
// and when redisplaying older bets/parlay legs whose stored description predates
// this format, so every screen (bet slip, pending/past bets, parlay legs) reads
// the same way regardless of when the bet was placed.

// Human-readable labels for GoalServe market keys
export const GAME_MARKET_LABELS = {
  // Main lines
  h2h: 'Moneyline', h2h_3way: '1X2 (Full Game)', spreads: 'Spread', totals: 'Total (O/U)', overtime: 'Overtime',
  // Half lines
  h2h_h1: '1st Half Moneyline', h2h_h2: '2nd Half Moneyline', h2h_3way_h1: '1st Half 1X2', h2h_3way_h2: '2nd Half 1X2',
  spreads_h1: '1st Half Spread', spreads_h2: '2nd Half Spread',
  totals_h1: '1st Half Total', totals_h2: '2nd Half Total',
  // Quarter lines
  h2h_q1: 'Q1 Moneyline', h2h_q2: 'Q2 Moneyline', h2h_q3: 'Q3 Moneyline', h2h_q4: 'Q4 Moneyline',
  h2h_3way_q1: 'Q1 1X2', h2h_3way_q2: 'Q2 1X2', h2h_3way_q3: 'Q3 1X2', h2h_3way_q4: 'Q4 1X2',
  spreads_q1: 'Q1 Spread', spreads_q2: 'Q2 Spread', spreads_q3: 'Q3 Spread', spreads_q4: 'Q4 Spread',
  totals_q1: 'Q1 Total', totals_q2: 'Q2 Total', totals_q3: 'Q3 Total', totals_q4: 'Q4 Total',
  // Per-map moneyline (esports)
  h2h_map1: 'Map 1 Moneyline', h2h_map2: 'Map 2 Moneyline', h2h_map3: 'Map 3 Moneyline',
  h2h_map4: 'Map 4 Moneyline', h2h_map5: 'Map 5 Moneyline',
  // Team totals
  team_total_home: 'Home Team Total', team_total_away: 'Away Team Total',
  team_total_home_h1: 'Home Total – 1st Half', team_total_away_h1: 'Away Total – 1st Half',
  team_total_home_h2: 'Home Total – 2nd Half', team_total_away_h2: 'Away Total – 2nd Half',
  team_total_home_ot: 'Home Total (incl. OT)', team_total_away_ot: 'Away Total (incl. OT)',
  team_total_home_q1: 'Home Q1 Points', team_total_away_q1: 'Away Q1 Points',
  team_total_home_q2: 'Home Q2 Points', team_total_away_q2: 'Away Q2 Points',
  team_total_home_q3: 'Home Q3 Points', team_total_away_q3: 'Away Q3 Points',
  team_total_home_q4: 'Home Q4 Points', team_total_away_q4: 'Away Q4 Points',
  // Double chance
  double_chance: 'Double Chance', double_chance_h1: 'Double Chance – 1H', double_chance_h2: 'Double Chance – 2H',
  double_chance_q1: 'Double Chance – Q1', double_chance_q2: 'Double Chance – Q2',
  double_chance_q3: 'Double Chance – Q3', double_chance_q4: 'Double Chance – Q4',
  // Odd/Even
  odd_even: 'Odd/Even', odd_even_ot: 'Odd/Even (incl. OT)',
  odd_even_h1: 'Odd/Even – 1st Half', odd_even_h2: 'Odd/Even – 2nd Half',
  odd_even_q1: 'Odd/Even – Q1', odd_even_q2: 'Odd/Even – Q2',
  odd_even_q3: 'Odd/Even – Q3', odd_even_q4: 'Odd/Even – Q4',
  odd_even_home: 'Home Team Odd/Even', odd_even_away: 'Away Team Odd/Even',
  odd_even_home_h2: 'Home Odd/Even – 2H', odd_even_away_h2: 'Away Odd/Even – 2H',
  odd_even_home_q1: 'Home Odd/Even – Q1', odd_even_away_q1: 'Away Odd/Even – Q1',
  odd_even_home_q2: 'Home Odd/Even – Q2', odd_even_away_q2: 'Away Odd/Even – Q2',
  odd_even_home_q3: 'Home Odd/Even – Q3', odd_even_away_q3: 'Away Odd/Even – Q3',
  odd_even_home_q4: 'Home Odd/Even – Q4', odd_even_away_q4: 'Away Odd/Even – Q4',
  odd_even_home_ot: 'Home Odd/Even (OT)', odd_even_away_ot: 'Away Odd/Even (OT)',
  // Race / Special
  race_to_20: 'Race to 20', race_to_30: 'Race to 30', race_to_40: 'Race to 40',
  race_to_50: 'Race to 50', race_to_100: 'Race to 100',
  highest_scoring_quarter: 'Highest Scoring Quarter', highest_scoring_half: 'Highest Scoring Half',
  team_highest_scoring_quarter: 'Team – Highest Scoring Qtr',
  ht_ft: 'Half-Time / Full-Time', ht_ft_ot: 'HT/FT (incl. OT)',
  result_total: 'Result + Total', result_total_h1: 'Result + Total – 1H',
  // Basketball player props
  player_points: 'Points', player_rebounds: 'Rebounds', player_assists: 'Assists',
  player_threes: '3-Pointers', player_blocks: 'Blocks', player_steals: 'Steals',
  player_turnovers: 'Turnovers', player_blocks_steals: 'Blk + Stl',
  player_points_rebounds_assists: 'Pts + Reb + Ast',
  player_points_rebounds: 'Pts + Reb', player_points_assists: 'Pts + Ast',
  player_rebounds_assists: 'Reb + Ast',
  player_double_double: 'Double-Double', player_triple_double: 'Triple-Double',
  player_first_basket: 'First Basket', player_first_assist: 'First Assist',
  player_first_rebound: 'First Rebound',
  // Basketball alternate lines
  player_points_alternate: 'Points (Alt Lines)',
  player_rebounds_alternate: 'Rebounds (Alt Lines)',
  player_assists_alternate: 'Assists (Alt Lines)',
  player_threes_alternate: '3-Pointers (Alt Lines)',
  player_blocks_alternate: 'Blocks (Alt Lines)',
  player_steals_alternate: 'Steals (Alt Lines)',
  player_points_rebounds_assists_alternate: 'Pts+Reb+Ast (Alt)',
  player_points_rebounds_alternate: 'Pts+Reb (Alt)',
  player_points_assists_alternate: 'Pts+Ast (Alt)',
  player_rebounds_assists_alternate: 'Reb+Ast (Alt)',
  // Baseball player props
  player_total_bases: 'Total Bases', player_hits: 'Hits',
  player_rbis: 'RBIs', player_home_runs: 'Home Runs',
  player_strikeouts: 'Strikeouts', player_runs: 'Runs Scored',
  player_total_bases_alternate: 'Total Bases (Alt)',
  player_hits_alternate: 'Hits (Alt)', player_strikeouts_alternate: 'Strikeouts (Alt)',
  // Hockey player props
  player_shots_on_goal: 'Shots on Goal', player_goals: 'Goals',
  player_saves: 'Saves', player_power_play_points: 'Power Play Points',
  player_goal_scorer_first: 'First Goalscorer',
  player_goal_scorer_anytime: 'Anytime Goalscorer',
  player_shots_on_goal_alternate: 'Shots on Goal (Alt)',
  player_goals_alternate: 'Goals (Alt)',
  // Football player props
  player_pass_yds: 'Passing Yards', player_rush_yds: 'Rushing Yards',
  player_reception_yds: 'Receiving Yards', player_pass_tds: 'Passing TDs',
  player_receptions: 'Receptions', player_tackles_assists: 'Tackles + Assists',
  player_sacks: 'Sacks', player_pass_interceptions: 'Interceptions',
  player_anytime_td: 'Anytime TD',
  player_pass_yds_alternate: 'Passing Yards (Alt)',
  player_rush_yds_alternate: 'Rushing Yards (Alt)',
  player_reception_yds_alternate: 'Receiving Yards (Alt)',
  player_receptions_alternate: 'Receptions (Alt)',
  // Soccer player props
  player_anytime_goalscorer: 'Anytime Goalscorer',
  player_shots_on_target: 'Shots on Target', player_shots_total: 'Total Shots',
  player_passes: 'Passes', player_cards: 'Cards',
  player_to_be_booked: 'To Be Booked',
  player_to_score_or_assist: 'To Score or Assist',
  player_goalkeeper_saves: 'Goalkeeper Saves',
  player_fouls_committed: 'Fouls Committed', player_fouled: 'Fouls Drawn',
  player_tackles_soccer: 'Tackles',

  // NHL – Period markets
  h2h_p1: 'P1 Moneyline', h2h_p2: 'P2 Moneyline', h2h_p3: 'P3 Moneyline',
  h2h_3way_p1: 'P1 3-Way', h2h_3way_p2: 'P2 3-Way', h2h_3way_p3: 'P3 3-Way',
  spreads_p1: 'P1 Spread', spreads_p2: 'P2 Spread', spreads_p3: 'P3 Spread',
  totals_p1: 'P1 Total', totals_p2: 'P2 Total', totals_p3: 'P3 Total',
  odd_even_p1: 'Odd/Even – P1', odd_even_p2: 'Odd/Even – P2', odd_even_p3: 'Odd/Even – P3',
  team_total_home_p1: 'Home P1 Goals', team_total_away_p1: 'Away P1 Goals',
  team_total_home_p2: 'Home P2 Goals', team_total_away_p2: 'Away P2 Goals',
  team_total_home_p3: 'Home P3 Goals', team_total_away_p3: 'Away P3 Goals',

  // Baseball – First 5 / inning markets
  h2h_f5: '1st 5 Inn Moneyline', spreads_f5: '1st 5 Inn Spread', totals_f5: '1st 5 Inn Total',
  totals_i1: '1st Inn Total', totals_i2: '2nd Inn Total', totals_i3: '3rd Inn Total',
  btts_i1: 'Both Score – 1st Inn', team_score_i1: 'Team Scores 1st Inn',

  // Soccer / Hockey – Goal specials
  btts: 'Both Teams to Score', btts_h1: 'BTTS – 1st Half', btts_h2: 'BTTS – 2nd Half',
  first_goal_team: 'Team to Score First', next_goal: 'Next Goal',
  clean_sheet_home: 'Home Clean Sheet', clean_sheet_away: 'Away Clean Sheet',
  result_btts: 'Result + BTTS', dc_btts: 'Double Chance + BTTS',
  // Soccer – Corners
  corners_total: 'Total Corners', corners_spread: 'Corners Handicap',
  corners_total_h1: 'Corners – 1st Half', corners_total_h2: 'Corners – 2nd Half',

  // Tennis – Set markets
  totals_sets: 'Total Sets', totals_games: 'Total Games', set_score: 'Correct Set Score',
  spreads_games: 'Games Handicap',
  h2h_s1: 'Set 1 Winner', h2h_s2: 'Set 2 Winner', h2h_s3: 'Set 3 Winner',
  h2h_s4: 'Set 4 Winner', h2h_s5: 'Set 5 Winner',
  totals_s1: 'Set 1 Games', totals_s2: 'Set 2 Games', totals_s3: 'Set 3 Games',
  spreads_s1: 'Set 1 Handicap', spreads_s2: 'Set 2 Handicap', spreads_s3: 'Set 3 Handicap',

  // MMA / Boxing – Fight markets
  method_of_victory: 'Method of Victory', round_betting: 'Round Betting',
  goes_distance: 'Fight Goes Distance', totals_rounds: 'Total Rounds (O/U)',
  round_group: 'Round Group Betting',

  // Golf – Outright markets
  outright_winner: 'Tournament Winner',
  round_1_leader: 'Round 1 Leader', round_2_leader: 'Round 2 Leader',
  round_3_leader: 'Round 3 Leader', round_4_leader: 'Round 4 Leader',
  top_5: 'Top 5 Finish', top_10: 'Top 10 Finish', top_20: 'Top 20 Finish',
  make_cut: 'Make / Miss Cut',
};

const getMarketLabel = (marketKey) =>
  GAME_MARKET_LABELS[marketKey] || marketKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Binary "will it happen" player props — the book still prices them as an
// Over/Under pair around a nominal 0.5 line (so a real line exists on the
// outcome), but there's no meaningful threshold to show a bettor: "+0.5" /
// "-0.5" reads as a stat line when it actually just means yes/no. Shown as
// Yes/No instead, everywhere a market's point gets displayed.
export const YES_NO_MARKET_KEYS = new Set([
  // Basketball
  'player_double_double', 'player_triple_double',
  'player_first_basket', 'player_first_team_basket', 'player_method_of_first_basket',
  // Football
  'player_anytime_td', 'player_1st_td', 'player_last_td',
  // Hockey
  'player_goal_scorer_anytime', 'player_goal_scorer_first', 'player_goal_scorer_last',
  // Baseball
  'batter_to_record_a_hit', 'batter_to_hit_a_home_run', 'batter_first_home_run',
  // Soccer
  'player_anytime_goalscorer', 'player_to_be_booked', 'player_to_score_or_assist',
]);

// Markets with a natural team-name subject where the market name itself would be
// redundant ("Moneyline: Atlanta Braves" -> just "Atlanta Braves").
const NO_LABEL_MARKETS = new Set(['h2h', 'h2h_3way', 'spreads']);
const DIRECTION_WORDS = new Set(['over', 'under', 'yes', 'no']);
// Some feeds give team-total outcomes a compound name like "Over Los Angeles
// Dodgers" instead of a bare "Over" — strip just the leading direction word so
// the team name survives (the sign in "(+1.5)" already says Over, so keeping
// the word too would be redundant on top of "Home Team Total:").
const DIRECTION_PREFIX = /^(over|under)\s+/i;

// { market_type, selection, player_name, selection_type, point } -> display text.
// Convention: "<label>: <who> (<signed point>)". The point's sign alone conveys
// Over(+)/Under(-) instead of spelling the word out, so no separator is needed
// before the parens; spreads keep whatever sign the API already gives them
// (favorite/underdog).
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// team_total_home[...] / team_total_away[...] always refer to a specific side
// — deriving the name from home_team/away_team is more reliable than trusting
// the outcome's own selection text (which, for bets placed before this format
// existed, can just be an alias of the old full stored description).
const impliedTeamFromMarket = (market_type, home_team, away_team) => {
  if (!market_type) return null;
  if (market_type.startsWith('team_total_home')) return home_team || null;
  if (market_type.startsWith('team_total_away')) return away_team || null;
  return null;
};

export const formatBetSelection = ({ market_type, selection, player_name, selection_type, point, home_team, away_team }) => {
  const marketLabel = market_type ? getMarketLabel(market_type) : '';
  let rawSelection = selection ? String(selection).trim() : '';

  // Some feeds bake the market's own label directly into the outcome name —
  // sometimes more than once (e.g. outcome.name = "Away Team Total: Away Team
  // Total: Over Kansas City Royals") — so prepending marketLabel below would
  // pile another copy on top. Strip every leading occurrence first, tolerating
  // whatever whitespace shows up around the colon.
  if (marketLabel) {
    const labelPrefix = new RegExp(`^${escapeRegExp(marketLabel)}\\s*:\\s*`, 'i');
    while (labelPrefix.test(rawSelection)) {
      rawSelection = rawSelection.replace(labelPrefix, '').trim();
    }
  }

  // Plain Over/Under total markets (game or period-level) never have a real
  // subject beyond the direction/point — there's no team or player to name —
  // so never trust whatever's in `selection` for these, even if it's not a
  // bare "Over"/"Under" (old stored bets can have anything in there).
  const hasNoSubject = market_type && market_type.startsWith('totals');

  let cleanedSelection = hasNoSubject || DIRECTION_WORDS.has(rawSelection.toLowerCase())
    ? ''
    : rawSelection.replace(DIRECTION_PREFIX, '');
  // Belt-and-suspenders: some feeds hand back an outcome name that, once
  // cleaned, is just the market's own label again with nothing else added
  // (e.g. a "1st Half Total" market whose outcome name is literally "1st Half
  // Total") — the prefix-strip above only catches a *leading* repeat, this
  // catches a full-string match regardless of how it got there.
  if (marketLabel && cleanedSelection.toLowerCase() === marketLabel.toLowerCase()) {
    cleanedSelection = '';
  }
  const who = player_name || impliedTeamFromMarket(market_type, home_team, away_team) || cleanedSelection;

  const showLabel = market_type && !NO_LABEL_MARKETS.has(market_type);
  const base = showLabel ? (who ? `${marketLabel}: ${who}` : marketLabel) : who;

  if (point == null || point === '') return base;

  const isSpread = market_type === 'spreads' || (market_type && market_type.startsWith('spreads_'));
  const isYesNo = market_type && YES_NO_MARKET_KEYS.has(market_type);

  let signedPoint;
  if (isYesNo) signedPoint = selection_type === 'under' ? 'No' : 'Yes';
  else if (isSpread) signedPoint = `${point > 0 ? '+' : ''}${point}`;
  else if (selection_type === 'over') signedPoint = `O ${Math.abs(point)}`;
  else if (selection_type === 'under') signedPoint = `U ${Math.abs(point)}`;
  else signedPoint = `${point > 0 ? '+' : ''}${point}`;

  return base ? `${base} (${signedPoint})` : `(${signedPoint})`;
};
